import React, { useCallback, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import styled, { css } from 'styled-components';
import { getExternalDevices } from '~/javascripts/admin/actions/devices';
import { useAppSelector } from '~/javascripts/admin/hooks/useApp';
import { selectUserPrivateMoney } from '~/javascripts/admin/reducers/user-private-money';
import { toQueryParams } from '~/javascripts/admin/utils';
import Button from '../../atoms/Buttons';
import Headings from '../../atoms/Headings';
import Label from '../../atoms/Label';
import TextBox from '../../atoms/TextBox';
import BreadCrumb from '../../molecules/BreadCrumb';
import SelectedPrivateMoneyDisplay from '../../organisms/SelectedPrivateMoneyDisplay';
import ListTable from './ListTable';

type DeviceKeysFormData = {
  shop_name: string | null;
};

type DeviceKeys = Awaited<ReturnType<typeof getExternalDevices>>;

const PageWrapper = styled.div``;

const HeadingWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    margin-top: ${spacing['16']};

    ${Headings.h5} {
      margin-bottom: ${spacing['0']};
    }
  `}
`;

const SectionWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    margin-left: ${spacing['8']};
  `}
`;

const FormInputWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    display: flex;
    gap: ${spacing['8']};
    align-items: center;
    margin-top: ${spacing['4']};

    ${Label} {
      margin-bottom: ${spacing['0']};
    }
  `}
`;

const ButtonsWrapper = styled.div`
  ${({ theme: { spacing } }) => css`
    display: flex;
    gap: ${spacing['8']};
    align-items: center;
    margin-top: ${spacing['12']};
  `}
`;

const CRUMBS = [
  { value: 'ホーム', href: '/', isCurrentPage: false },
  { value: '各種設定', href: '', isCurrentPage: true },
  { value: '社外デバイス管理', href: '', isCurrentPage: true },
];

const DeviceKeys = () => {
  const history = useHistory();
  const user = useAppSelector((state) => state.user);
  const { id: privateMoneyId, organization: privateMoneyOrg } =
    useAppSelector(selectUserPrivateMoney);

  const [deviceKeys, setDeviceKeys] = useState<DeviceKeys>();

  const { handleSubmit, register, reset } = useForm<DeviceKeysFormData>({
    defaultValues: {
      shop_name: '',
    },
  });

  const isMember =
    privateMoneyOrg?.code !== 'pocketchange' && privateMoneyOrg?.code !== user.organization?.code;

  const fetchDeviceKeys = useCallback(
    (queryLink = '', formData?: DeviceKeysFormData) => {
      if (privateMoneyId) {
        const queryString = !!queryLink
          ? queryLink
          : toQueryParams(
              {
                private_money_id: privateMoneyId,
                per_page: 50,
                ...formData,
              },
              true
            );

        getExternalDevices(queryString).then((res) => setDeviceKeys(res));
      }
    },
    [privateMoneyId]
  );

  const onChangePage = (paginatedQuery: string | null) =>
    fetchDeviceKeys(paginatedQuery ?? undefined);

  const onConfirm = (data: DeviceKeysFormData) => {
    fetchDeviceKeys('', data);
  };

  const clearFilters = () => {
    reset({
      shop_name: '',
    });
  };

  useEffect(() => {
    fetchDeviceKeys();
  }, [privateMoneyId, fetchDeviceKeys]);

  return (
    <PageWrapper>
      <BreadCrumb crumbs={CRUMBS} />
      {!isMember && (
        <Button onClick={() => history.push('/settings/device-keys/create')}>新規登録</Button>
      )}
      <HeadingWrapper>
        <Headings.h5>検索条件</Headings.h5>
      </HeadingWrapper>
      <SectionWrapper>
        <SelectedPrivateMoneyDisplay />
        <FormInputWrapper>
          <Label>店舗名</Label>
          <TextBox {...register('shop_name')} />
        </FormInputWrapper>
        <ButtonsWrapper>
          <Button variant="neutral" onClick={clearFilters}>
            クリア
          </Button>
          <Button onClick={handleSubmit(onConfirm)}>検索</Button>
        </ButtonsWrapper>
      </SectionWrapper>
      <HeadingWrapper>
        <Headings.h5>検索結果</Headings.h5>
      </HeadingWrapper>
      <ListTable deviceKeys={deviceKeys} onChangePage={onChangePage} />
    </PageWrapper>
  );
};

export default DeviceKeys;
